import { useContext, useState } from "react";
import Container from "@mui/material/Container";
import dayjs from "dayjs";
import { OrganizationContext, useAllLabRequisitionsList } from "hhn-commons";
import { ILabRequisitionsQueryParams } from "@home-health-notify/models";
import { LabRequisitionsTable } from "./LabRequisitionsTable";
import LabRequisitionsFilter from "./LabRequisitionsFilter";

const LAB_REQUISITIONS_LIMIT = 15;

interface FilterState {
  fromDate?: dayjs.Dayjs;
  toDate?: dayjs.Dayjs;
  status?: string;
}

function buildParams(
  filter: FilterState,
  search: string,
  limit: number,
  cursor?: string,
): ILabRequisitionsQueryParams {
  const params: ILabRequisitionsQueryParams = { limit, cursor };

  const hasDateRange = filter.fromDate && filter.toDate;
  const hasStatus = filter.status !== undefined;

  if (hasDateRange || hasStatus) {
    params.filters = {};
    if (hasDateRange) {
      params.filters.requisitionDateRange = {
        from: filter.fromDate!.format("MM/DD/YYYY"),
        to: filter.toDate!.format("MM/DD/YYYY"),
      };
    }
    if (hasStatus) {
      params.filters.requisitionStatuses = [filter.status!];
    }
  }

  if (search) {
    params.search = search;
  }

  return params;
}

// Rendered as a section inside the "More" workspace (no own chrome/close).
export function LabRequisitionsListing() {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const orgId = selectedOrgUser?.getOrganization().organizationId ?? "";

  const [filter, setFilter] = useState<FilterState>({});
  const [appliedFilter, setAppliedFilter] = useState<FilterState>({});
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LAB_REQUISITIONS_LIMIT);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [params, setParams] = useState<ILabRequisitionsQueryParams>({
    limit: LAB_REQUISITIONS_LIMIT,
  });

  const { allLabRequisitions, isLoading } = useAllLabRequisitionsList(params);

  const resetPagination = () => {
    setPage(0);
    setCursors([undefined]);
  };

  const onFilterClick = () => {
    const hasChanged =
      appliedFilter.fromDate !== filter.fromDate ||
      appliedFilter.toDate !== filter.toDate ||
      appliedFilter.status !== filter.status;

    if (hasChanged) {
      resetPagination();
      setAppliedFilter(filter);
      setParams(buildParams(filter, search, rowsPerPage));
    }
  };

  const onResetClick = () => {
    setFilter({});
    setAppliedFilter({});
    setSearch("");
    resetPagination();
    setParams({ limit: rowsPerPage });
  };

  const onSetPage = (newPage: number) => {
    if (newPage > page && allLabRequisitions.nextCursor) {
      const newCursors = [...cursors];
      newCursors[newPage] = allLabRequisitions.nextCursor;
      setCursors(newCursors);
      setParams(
        buildParams(
          appliedFilter,
          search,
          rowsPerPage,
          allLabRequisitions.nextCursor,
        ),
      );
    } else if (newPage < page) {
      setParams(
        buildParams(appliedFilter, search, rowsPerPage, cursors[newPage]),
      );
    }
    setPage(newPage);
  };

  const onPageLimitChange = (pageLimit: number) => {
    resetPagination();
    setRowsPerPage(pageLimit);
    setParams(buildParams(appliedFilter, search, pageLimit));
  };

  const onSetFromDate = (value: dayjs.Dayjs) => {
    setFilter(prev => ({ ...prev, fromDate: value }));
  };

  const onSetToDate = (value: dayjs.Dayjs) => {
    setFilter(prev => ({ ...prev, toDate: value }));
  };

  const onSetStatusFilter = (value: string | undefined) => {
    setFilter(prev => ({ ...prev, status: value }));
  };

  const onSearch = (value: string) => {
    setSearch(value);
    resetPagination();
    setParams(buildParams(appliedFilter, value, rowsPerPage));
  };

  return (
    <Container data-testid="lab-requisitions">
      <LabRequisitionsFilter
        fromDate={filter.fromDate}
        onSetFromDate={onSetFromDate}
        toDate={filter.toDate}
        onSetToDate={onSetToDate}
        status={filter.status}
        onSetStatus={onSetStatusFilter}
        onFilterButtonClick={onFilterClick}
        onResetButtonClick={onResetClick}
        onSearch={onSearch}
      />
      <LabRequisitionsTable
        page={page}
        onSetPage={onSetPage}
        rowsPerPage={rowsPerPage}
        onPageLimitChange={onPageLimitChange}
        labRequisitions={allLabRequisitions}
        isLoading={isLoading}
        orgId={orgId}
      />
    </Container>
  );
}
